import { Injectable, computed, inject, signal } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { WidgetType } from '@frontend/sports/types/components/widget';
import { WidgetRegistryService } from '@frontend/sports/widget/core/feature';
import { Subject, filter, map, switchMap } from 'rxjs';

import { FavouritesDashboardService, FixturesRequest } from '../favourites-dashboard/favourites-dashboard.service';
import { FavouritesGridNavigation } from './favourites-grid.model';

@Injectable({ providedIn: 'root' })
export class FavouritesGridWidgetService {
    private readonly favouritesDashboardService = inject(FavouritesDashboardService);
    private readonly widgetRegistryService = inject(WidgetRegistryService);
    private readonly favouritesConfig = inject(FavouritesConfig);

    private widgetFixtures = signal<{ [sportId: string]: Fixture[] }>({});
    private triggerLoading$ = new Subject<string>();
    private _widgetNavigation = signal<FavouritesGridNavigation[]>([]);

    widgetNavigation = this._widgetNavigation.asReadonly();
    widgetActiveTab = computed(() => this.widgetNavigation().find((tab) => tab.active));
    widgetActiveFixtures = computed(() => {
        const activeSport = this.widgetActiveTab();
        if (!activeSport) {
            return [];
        }
        const fixtures = this.widgetFixtures();
        return fixtures[activeSport.itemId] ?? [];
    });

    constructor() {
        this.triggerLoading$
            .pipe(
                filter((sportId) => !this.widgetFixtures()[sportId]),
                switchMap((sportId) => {
                    const request: FixturesRequest = {
                        take: this.favouritesConfig.favouritesGridWidgetFixturesMaxCount,
                        favouritedItems: this.widgetActiveTab()?.additionalFilters ?? {},
                    };

                    return this.favouritesDashboardService.getFixtures(request).pipe(
                        map((fixturePages) => fixturePages?.[0]?.[sportId]?.fixtures),
                        filter(isDefined),
                        map((fixtures) => ({ sportId, fixtures })),
                    );
                }),
            )
            .subscribe((sportFixtures) => {
                this.widgetFixtures.set({ ...this.widgetFixtures(), [sportFixtures.sportId]: sportFixtures.fixtures });

                this.lazyRegisterFavouritesGridWidget();
            });
    }

    loadActiveSport(sportId: string) {
        this._widgetNavigation.set(this.widgetNavigation().map((tab) => ({ ...tab, active: tab.itemId === sportId })));
        this.triggerLoading$.next(sportId);
    }

    cleanUpWidgetCache() {
        this._widgetNavigation.set([]);
        this.widgetFixtures.set({});
        this.widgetRegistryService.remove(WidgetType.FavouritesGrid);
    }

    setWidgetNavigation(favouritesGridNavigation: FavouritesGridNavigation[]) {
        this._widgetNavigation.set(favouritesGridNavigation);
    }

    private async lazyRegisterFavouritesGridWidget() {
        if (!this.widgetRegistryService.get(WidgetType.FavouritesGrid)) {
            const { FavouritesGridWidgetComponent } = await import(
                /* webpackChunkName: "ms-favourites-grid-widget" */ './favourites-grid-widget.component'
            );
            this.widgetRegistryService.registerLazy(WidgetType.FavouritesGrid, FavouritesGridWidgetComponent);
        }
    }
}
